import { FileAudio, RefreshCw, Clock, User, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import type { Call } from '../types';
import { formatDateTime, formatDuration, truncate } from '../utils/format';

interface TranscriptsProps {
  calls: Call[];
  loading: boolean;
  onRefresh: () => void;
  onCallClick: (call: Call) => void;
}

export default function Transcripts({
  calls,
  loading,
  onRefresh,
  onCallClick,
}: TranscriptsProps) {
  // Filter calls with transcripts
  const callsWithTranscript = calls.filter((c) => c.transcript);

  if (loading && callsWithTranscript.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Lade Transkripte...</p>
        </div>
      </div>
    );
  }

  if (callsWithTranscript.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <FileAudio className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Keine Transkripte
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Keine Transkripte vorhanden.
          </p>
          <button
            onClick={onRefresh}
            className="mt-4 btn-secondary"
            disabled={loading}
          >
            <RefreshCw className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Aktualisieren
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Transkripte</h2>
          <p className="text-sm text-gray-500">
            {callsWithTranscript.length} Transkripte verfugbar
          </p>
        </div>
        <button
          onClick={onRefresh}
          className="btn-secondary"
          disabled={loading}
        >
          <RefreshCw className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')} />
          Aktualisieren
        </button>
      </div>

      {/* Transcript List */}
      <div className="space-y-4">
        {callsWithTranscript.map((call) => (
          <div
            key={call.id}
            className="card p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onCallClick(call)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                {/* Header */}
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-sm font-medium text-gray-900 truncate">
                    {call.company_name || call.contact_name || 'Unbekannt'}
                  </h3>
                  {call.transcript_source && (
                    <span className="badge badge-blue text-xs">
                      {call.transcript_source}
                    </span>
                  )}
                </div>

                {/* Meta */}
                <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatDateTime(call.started_at)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span>{formatDuration(call.duration_seconds)}</span>
                  </div>
                  {call.agent_name && (
                    <div className="flex items-center space-x-1">
                      <User className="w-3 h-3" />
                      <span>{call.agent_name}</span>
                    </div>
                  )}
                </div>

                {/* Preview */}
                <p className="text-sm text-gray-600">
                  {truncate(call.transcript || '', 200)}
                </p>
              </div>

              <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
